/**
 * Query Client Provider Component
 *
 * Wraps the application with TanStack Query's QueryClientProvider
 * to enable data fetching and caching throughout the app.
 *
 * @module providers/QueryClientProvider
 */

import { QueryClient, QueryClientProvider as Provider } from '@tanstack/react-query'
import { ReactNode, useState } from 'react'

const hydrationKey = 'tanstack-query-hydration'

/**
 * Provider component that creates and manages the QueryClient instance.
 *
 * Creates a new QueryClient on mount (client-side only) and hydrates
 * it with any server-provided state. This should wrap your entire app.
 *
 * @param {Object} props
 * @param {ReactNode} props.children - The application components
 * @returns {React.ReactElement}
 */
export function QueryClientProvider({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            gcTime: 1000 * 60 * 30, // 30 minutes
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      })
  )

  return <Provider client={queryClient}>{children}</Provider>
}

/**
 * Hydrates the QueryClient with server-provided state.
 * Call this in your entry-client.tsx before rendering.
 *
 * @param {object} state - The hydration state from the server
 */
export function hydrateQueries(state: any) {
  if (typeof window !== 'undefined' && state) {
    try {
      const queryClient = new QueryClient()
      queryClient.hydrate(state)
      // Store on window for access if needed
      ;(window as any)[hydrationKey] = queryClient
    } catch (e) {
      console.warn('Failed to hydrate TanStack Query:', e)
    }
  }
}

/**
 * Gets the hydrated QueryClient instance if available.
 * Useful for server-to-client hydration.
 *
 * @returns {QueryClient | undefined}
 */
export function getHydratedQueryClient() {
  if (typeof window !== 'undefined') {
    return (window as any)[hydrationKey] as QueryClient | undefined
  }
  return undefined
}
